package app.footyguide.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.MatchStatus
import app.footyguide.domain.model.Team
import app.footyguide.domain.model.isLive

object CrestSizes {
    const val STANDINGS_ROW_PX = 22
    const val STANDINGS_ROW_FALLBACK = 18
    const val STANDINGS_ROW_CSS = "clamp(20px, 5vw, 22px)"

    const val TOOLBAR_PX = 26
    const val TOOLBAR_FALLBACK = 22
    const val TOOLBAR_CSS = "clamp(24px, 6vw, 26px)"

    const val MATCH_CARD_PX = 30
    const val MATCH_CARD_FALLBACK = 24
    const val MATCH_CARD_CSS = "clamp(28px, 7.5vw, 30px)"

    const val HERO_CARD_PX = 36
    const val HERO_CARD_FALLBACK = 30
    const val HERO_CARD_CSS = "clamp(34px, 8.5vw, 38px)"

    const val DETAIL_HEADER_PX = 52
    const val DETAIL_HEADER_FALLBACK = 42
    const val DETAIL_HEADER_CSS = "clamp(44px, 11vw, 52px)"
}

private const val TEAM_LOGO_SIZE = CrestSizes.MATCH_CARD_PX
private const val TEAM_NAME_DEFAULT_FONT_SIZE = 14
private const val TEAM_NAME_MEDIUM_FONT_SIZE = 13
private const val TEAM_NAME_SMALL_FONT_SIZE = 12

@Composable
fun MatchCard(
    game: Game,
    homeSigla: String,
    awaySigla: String,
    homeLogo: String?,
    awayLogo: String?,
    homeTeam: Team? = null,
    awayTeam: Team? = null,
    channelLogos: Map<String, String?> = emptyMap(),
    onClick: () -> Unit
) {
    val strings = app.footyguide.core.locale.LocalStrings.current
    Div({
        classes("fg-card-interactive", "fg-animated-card")
        onClick { onClick() }
        style {
            backgroundColor(Color("var(--surface)"))
            border(1.px, LineStyle.Solid, Color("var(--border)"))
            borderRadius(14.px)
            padding(14.px, 16.px)
            marginBottom(10.px)
            cursor("pointer")
            property("box-shadow", "0 2px 4px rgba(22, 24, 29, 0.04), 0 8px 16px rgba(22, 24, 29, 0.03)")
        }
    }) {
        // Header: Status and Channels
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            if (game.isLive) {
                // LIVE indicator with radar pulse
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                    }
                }) {
                    Span({
                        classes("fg-live-pulse-dot")
                    })
                    Span({
                        style {
                            fontFamily("Sora, sans-serif")
                            fontSize(11.px)
                            color(Color("#ef4444"))
                            fontWeight(800)
                            letterSpacing(0.5.px)
                        }
                    }) { Text(strings.liveLabel) }
                }
            } else {
                Span({
                    style {
                        fontFamily("Inter, sans-serif")
                        fontSize(11.px)
                        color(Color("var(--muted)"))
                        fontWeight(600)
                        letterSpacing(0.3.px)
                        property("white-space", "nowrap")
                    }
                }) {
                    val compGroup = game.group
                    val compStage = game.stage
                    val stageRound = compStage
                        ?.takeIf { it.contains("regular", ignoreCase = true) && it.contains("season", ignoreCase = true) }
                        ?.substringAfterLast("-", "")
                        ?.trim()
                        ?.toIntOrNull()
                    val competitionInfo = when {
                        game.matchday != null -> strings.formatRoundNumber(game.matchday!!).uppercase()
                        stageRound != null -> strings.formatRoundNumber(stageRound).uppercase()
                        compGroup != null -> compGroup.uppercase()
                        compStage != null && compStage != "REGULAR_SEASON" && !compStage.startsWith("Regular Season", ignoreCase = true) ->
                            compStage.replace("_", " ").uppercase()
                        else -> ""
                    }
                    
                    val time = try {
                        val kickoffStr = game.kickoff
                        val date = kotlin.js.Date(kickoffStr)
                        val options = js("({ hour: '2-digit', minute: '2-digit', hour12: false })")
                        date.toLocaleTimeString(strings.localeTag, options)
                    } catch (e: Exception) {
                        game.kickoff.substringAfter("T").take(5)
                    }
                    
                    val finalLabel = if (competitionInfo.isNotEmpty()) "$competitionInfo · $time" else time
                    Text(finalLabel)
                }
            }

            val channels = game.channelsFor("BR")
            if (channels.isNotEmpty()) {
                ChannelBadgesRow(channels)
            }
        }

        // Match Row (3 Anchors Layout)
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginTop(16.px)
                gap(6.px)
                width(100.percent)
            }
        }) {
            renderTeamAnchor(
                label = homeTeam?.shortName ?: homeSigla,
                logo = homeLogo,
                alignRight = false,
            )

            // ANCHOR 2: VS / SCORE (Centered)
            Div({
                style { 
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    flexShrink(0)
                    width(72.px)
                    minWidth(72.px)
                    padding(0.px, 6.px)
                }
            }) {
                if (game.matchStatus == MatchStatus.NOT_STARTED) {
                    Span({
                        style {
                            fontFamily("Sora, sans-serif")
                            fontSize(12.px)
                            color(Color("var(--muted)"))
                            fontWeight(700)
                            letterSpacing(1.px)
                        }
                    }) { Text("VS") }
                } else {
                    Span({
                        style {
                            fontFamily("Sora, sans-serif")
                            property("font-variant-numeric", "tabular-nums")
                            fontWeight(800)
                            fontSize(22.px)
                            letterSpacing((-0.5).px)
                            color(Color("var(--fg)"))
                        }
                    }) {
                        Text("${game.homeScore ?: 0} – ${game.awayScore ?: 0}")
                    }
                }
            }

            renderTeamAnchor(
                label = awayTeam?.shortName ?: awaySigla,
                logo = awayLogo,
                alignRight = true,
            )
        }
    }
}

@Composable
private fun renderTeamAnchor(
    label: String,
    logo: String?,
    alignRight: Boolean,
) {
    Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(if (alignRight) FlexDirection.RowReverse else FlexDirection.Row)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.FlexStart)
                width(44.percent)
                minWidth(0.px)
                gap(8.px)
            }
        }) {
        Div({
            style {
                width(TEAM_LOGO_SIZE.px)
                height(TEAM_LOGO_SIZE.px)
                property("width", CrestSizes.MATCH_CARD_CSS)
                property("height", CrestSizes.MATCH_CARD_CSS)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexShrink(0)
            }
        }) {
            SafeImg(
                src = logo,
                fallbackIcon = Icons.SHIELD,
                fallbackSize = CrestSizes.MATCH_CARD_FALLBACK,
                modifier = {
                    width(100.percent)
                    height(100.percent)
                    property("object-fit", "contain")
                    property("object-position", "center")
                }
            )
        }

            Div({
                style {
                    flexGrow(1)
                    minWidth(0.px)
                    minHeight(2.2.em)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(if (alignRight) JustifyContent.FlexEnd else JustifyContent.FlexStart)
                    textAlign(if (alignRight) "right" else "left")
            }
        }) {
            Span({
                style {
                    display(DisplayStyle.Block)
                    width(100.percent)
                    fontFamily("Sora, sans-serif")
                    property("font-size", "${teamNameFontSizePx(label)}px")
                    fontWeight(700)
                    letterSpacing((-0.3).px)
                    color(Color("var(--fg)"))
                    lineHeight(1.2.em)
                    property("display", "-webkit-box")
                    property("-webkit-box-orient", "vertical")
                    property("-webkit-line-clamp", "2")
                    whiteSpace("normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("hyphens", "auto")
                }
            }) {
                Text(label)
            }
        }
    }
}

internal fun teamNameFontSizePx(name: String): Int {
    return when {
        name.length > 24 -> TEAM_NAME_SMALL_FONT_SIZE
        name.length > 18 -> TEAM_NAME_MEDIUM_FONT_SIZE
        else -> TEAM_NAME_DEFAULT_FONT_SIZE
    }
}
